/* ==========================================================================
   BuildDirect — Checkout events and the samples-page door
   --------------------------------------------------------------------------
   Two jobs, on every page, both about the moment a shopper leaves the cart.

   1. Measure it. GA4's funnel is view_cart → begin_checkout → purchase, and
      begin_checkout was arriving only from Shopify's own checkout pixel — a
      sandboxed web pixel, subject to consent and blockers, and counted against
      a GA4 property that is loaded from two places (docs/tracking-cleanup-
      runbook-2026-08.md). Cart→order was therefore unreadable. This fires the
      GA4-shaped events from the theme, at the click, on every entry to
      checkout there is:

        | Event          | Fires on                                              |
        |----------------|-------------------------------------------------------|
        | view_cart      | the drawer opening; the cart page loading with lines  |
        | begin_checkout | the checkout button (drawer or cart page), a wallet    |
        |                | button (Shop Pay, PayPal, Apple Pay), the samples-page |
        |                | button, and the samples page's own form               |

      Both carry `ecommerce: { currency, value, items }` in GA4 shape, read
      from the JSON snippets/cart-items.liquid publishes inside <cart-items> —
      synchronously, because a fetch to /cart.js at the click loses the race
      with the navigation the click started. Every item carries bd_line_kind
      ('sample' | 'product' | 'fee') so a sample request and a pallet stay
      distinguishable downstream (audit C7). Alongside the ecommerce object:
      cart_mode, cart_sample_lines, cart_product_lines, cart_item_count,
      cart_value_cents, checkout_type ('standard' | 'express' | 'samples_page'
      | 'samples_express') and checkout_surface ('drawer' | 'cart_page' |
      'samples_page').

      Deduped: the same cart, surface and type within three seconds is one
      begin_checkout. That is what makes a double-click, and the synthetic
      re-click assets/bd-sample-shipping-fee.js makes after vetting a Meta
      cart, count once. GTM still needs a GA4 event tag on each of these
      custom-event triggers; the runbook has the steps.

   2. Open the door. A free-samples cart's button is a door to the theme's own
      samples-only checkout page rather than a submit (snippets/cart-drawer
      .liquid renders it with data-bd-samples-checkout). This follows it. It
      keeps name="checkout" so assets/bd-sample-gate.js can hold it and the
      shipping-fee vet still runs; when that vet is about to run (Meta cookie,
      a trusted click, not yet vetted) this stands aside and follows the
      synthetic click that continues the journey instead — which is why
      layout/theme.liquid loads this file before the fee script: both listen
      in the capture phase, and this one has to look first.

   Also mirrors the cart's kind onto the cart page (.cc-main-cart gets
   data-cart-mode), the way assets/bd-cart-drawer.js does for the drawer, so
   assets/bd-cart-samples.css can key on it there too.
   ========================================================================== */

(function () {
  if (window.__bdCheckoutEventsInit) return;
  window.__bdCheckoutEventsInit = true;

  var DEDUPE_MS = 3000;
  var META_COOKIE = 'bd_meta_src';
  var last = { signature: '', at: 0 };

  /* ------------------------------------------------------------- reading */

  function int(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  /* The cart surface an element belongs to. Each surface carries its own
     <cart-items> token and items JSON, and the cart page can carry the
     drawer's too, so the nearest one wins. */
  function scopeFor(el) {
    if (el && typeof el.closest === 'function') {
      var scope = el.closest('cart-drawer, .cc-main-cart, [data-bd-samples-checkout]');
      if (scope) return scope;
    }
    return document.querySelector('.cc-main-cart, [data-bd-samples-checkout]') || document.getElementById('cart-drawer') || document;
  }

  function surfaceFor(scope) {
    if (!scope || scope === document) return 'page';
    if (scope.tagName === 'CART-DRAWER') return 'drawer';
    if (scope.hasAttribute('data-bd-samples-checkout')) return 'samples_page';
    return 'cart_page';
  }

  function state(scope) {
    var el = scope.querySelector('[data-bd-cart-mode]');
    if (!el) {
      return { cart_mode: 'empty', cart_sample_lines: 0, cart_product_lines: 0, cart_item_count: 0, cart_value_cents: 0, cart_currency: '' };
    }
    return {
      cart_mode: el.getAttribute('data-bd-cart-mode') || 'empty',
      cart_sample_lines: int(el.getAttribute('data-bd-cart-samples')),
      cart_product_lines: int(el.getAttribute('data-bd-cart-products')),
      cart_item_count: int(el.getAttribute('data-bd-cart-count')),
      cart_value_cents: int(el.getAttribute('data-bd-cart-value')),
      cart_currency: el.getAttribute('data-bd-cart-currency') || ''
    };
  }

  function items(scope) {
    var el = scope.querySelector('[data-bd-cart-items]');
    if (!el) return [];
    try {
      var parsed = JSON.parse(el.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function read(el) {
    var scope = scopeFor(el);
    var st = state(scope);
    st.items = items(scope);
    st.checkout_surface = surfaceFor(scope);
    return st;
  }

  /* ------------------------------------------------------------- pushing */

  function push(event, el, extra) {
    window.dataLayer = window.dataLayer || [];
    var st = read(el);
    var lines = st.items;
    delete st.items;

    // GA4 asks for the previous ecommerce object to be cleared so an item list
    // from an earlier event cannot leak into this one.
    window.dataLayer.push({ ecommerce: null });

    var data = {
      event: event,
      ecommerce: {
        currency: st.cart_currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        value: Math.round(st.cart_value_cents) / 100,
        items: lines
      }
    };
    delete st.cart_currency;
    for (var k in st) if (Object.prototype.hasOwnProperty.call(st, k)) data[k] = st[k];
    for (var j in extra || {}) if (Object.prototype.hasOwnProperty.call(extra, j)) data[j] = extra[j];

    window.dataLayer.push(data);
    return data;
  }

  function signatureOf(data) {
    return [
      data.checkout_surface,
      data.checkout_type,
      data.ecommerce.items.map(function (i) { return i.bd_variant_id + 'x' + i.quantity; }).join(',')
    ].join('|');
  }

  /**
   * begin_checkout, once per cart, surface and type within DEDUPE_MS.
   * @param {Element} el - Where the click happened; decides the surface.
   * @param {object} [extra] - checkout_type and anything else to carry.
   * @returns {?object} the pushed payload, or null when deduped
   */
  function beginCheckout(el, extra) {
    var st = read(el);
    var probe = {
      checkout_surface: st.checkout_surface,
      checkout_type: (extra && extra.checkout_type) || 'standard',
      ecommerce: { items: st.items }
    };
    var signature = signatureOf(probe);
    var now = Date.now();
    if (signature === last.signature && now - last.at < DEDUPE_MS) return null;
    last = { signature: signature, at: now };

    var payload = { checkout_type: probe.checkout_type };
    for (var k in extra || {}) if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k];
    return push('begin_checkout', el, payload);
  }

  function viewCart(el, extra) {
    var st = read(el);
    if (st.cart_item_count === 0 && !st.items.length) return null;
    return push('view_cart', el, extra);
  }

  /* -------------------------------------------------------------- clicks */

  function hasCookie(name) {
    return new RegExp('(?:^|;\\s*)' + name + '=').test(document.cookie);
  }

  function isHeld(btn) {
    return btn.disabled || btn.getAttribute('aria-disabled') === 'true';
  }

  document.addEventListener(
    'click',
    function (evt) {
      var target = evt.target;
      if (!target || typeof target.closest !== 'function') return;

      var btn = target.closest('[name="checkout"]');
      if (btn) {
        if (isHeld(btn)) return;

        var samplesUrl = btn.getAttribute('data-bd-samples-checkout');
        beginCheckout(btn, { checkout_type: samplesUrl ? 'samples_page' : 'standard' });

        if (!samplesUrl) return;

        // The shipping-fee script is about to intercept this click, vet the
        // cart and re-click. Let it: the synthetic click is the one to follow.
        var feeWillVet = window.__bdSampleShippingFeeInit
          && hasCookie(META_COOKIE)
          && evt.isTrusted
          && btn.dataset.bdSampleFeeVetted !== '1';
        if (feeWillVet) return;

        evt.preventDefault();
        window.location.assign(samplesUrl);
        return;
      }

      // The wallets render inside .dynamic-cart-btns (shadow roots included —
      // a click inside one is retargeted to the host, which is inside here).
      var wallet = target.closest('.dynamic-cart-btns');
      if (wallet && !wallet.hidden) {
        beginCheckout(wallet, { checkout_type: 'express' });
      }
    },
    true
  );

  /* ------------------------------------------------------------ the page */

  function mirrorCartPage() {
    var page = document.querySelector('.cc-main-cart');
    if (!page) return;
    var mode = state(page).cart_mode;
    if (page.getAttribute('data-cart-mode') !== mode) page.setAttribute('data-cart-mode', mode);
  }

  function watchDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    var wasOpen = drawer.hasAttribute('open');
    new MutationObserver(function () {
      var isOpen = drawer.hasAttribute('open');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (isOpen) viewCart(drawer);
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  function init() {
    mirrorCartPage();
    watchDrawer();

    var page = document.querySelector('.cc-main-cart');
    if (page && page.querySelector('cart-items[data-empty="false"]')) viewCart(page);

    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        mirrorCartPage();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.bdCheckoutEvents = { beginCheckout: beginCheckout, viewCart: viewCart, read: read };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
